import React, { useCallback, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useSelector } from 'react-redux';
import { AppCard, AppRemixIcon } from '@/layout/AppComponents';
import { UnmountClosed } from 'react-collapse/lib/UnmountClosed';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import Moment from 'moment';
import ReactDOMServer from 'react-dom/server';
import colors from '../../../../../../constants/colors';
import IconListBlock from '../CardElements/IconListBlock';
import ExpandedContainer from '../ExpandedTabs/ExpandedContainer';
import Translator from '../../../../../components/helpers/Translator';

export default function TaskDefault(props) {
  const { taskID } = props;
  const { moduleBridgeHex } = useParams();
  const modulePermissions = useSelector(
    (s) => s.module[moduleBridgeHex]?.permissions?.correctiveActions
  );
  const userBridgePermissions = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions
  );
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const level2 = useSelector((state) => state.currentPage?.levels?.level2);
  const [isOpened, setIsOpened] = useState(false);

  const stateColor = useCallback(() => {
    let color = colors?.caTaskStates[3];

    if (level1?.[taskID]?.closedStamp) {
      color = colors?.caTaskStates[0];
    } else if (
      level1?.[taskID]?.limitStamp &&
      Moment(level1?.[taskID]?.limitStamp, 'DD/MM/YYYY').isBefore(Moment())
    ) {
      color = colors?.caTaskStates[2];
    } else if (
      level1?.[taskID]?.limitStamp &&
      Moment(level1?.[taskID]?.limitStamp, 'DD/MM/YYYY').isBefore(
        Moment().add(20, 'days')
      )
    ) {
      color = colors?.caTaskStates[1];
    }

    return color;
  }, [taskID, level1]);

  const getReview = useCallback(() => {
    const starColor = colors.success;
    let hasColorAndFilled;

    if (level1?.[taskID]?.reviewValue === null)
      return Array.from({ length: 5 }, (_, i) => (
        <AppRemixIcon key={i} icon="star" />
      ));

    return Array.from({ length: 5 }, (_, i) => {
      switch (i) {
        case 0:
          return <AppRemixIcon key={i} icon="star" filled color={starColor} />;
        case 1:
          hasColorAndFilled = [2, 2.5, 3, 3.5, 4, 4.5, 5].includes(
            level1?.[taskID]?.reviewValue
          );
          return (
            <AppRemixIcon
              key={i}
              icon={
                level1?.[taskID]?.reviewValue === 1.5 ? 'star-half' : 'star'
              }
              filled={hasColorAndFilled}
              color={hasColorAndFilled ? starColor : undefined}
            />
          );
        case 2:
          hasColorAndFilled = [3, 3.5, 4, 4.5, 5].includes(
            level1?.[taskID]?.reviewValue
          );
          return (
            <AppRemixIcon
              key={i}
              icon={
                level1?.[taskID]?.reviewValue === 2.5 ? 'star-half' : 'star'
              }
              filled={hasColorAndFilled}
              color={hasColorAndFilled ? starColor : undefined}
            />
          );
        case 3:
          hasColorAndFilled = [4, 4.5, 5].includes(
            level1?.[taskID]?.reviewValue
          );
          return (
            <AppRemixIcon
              key={i}
              icon={
                level1?.[taskID]?.reviewValue === 3.5 ? 'star-half' : 'star'
              }
              filled={hasColorAndFilled}
              color={hasColorAndFilled ? starColor : undefined}
            />
          );
        case 4:
          hasColorAndFilled = [5].includes(level1?.[taskID]?.reviewValue);
          return (
            <AppRemixIcon
              key={i}
              icon={
                level1?.[taskID]?.reviewValue === 4.5 ? 'star-half' : 'star'
              }
              filled={hasColorAndFilled}
              color={hasColorAndFilled ? starColor : undefined}
            />
          );
      }
    });
  }, [level1]);

  if (!level1) {
    return (
      <div className="card task-default">
        <div className="card-body" />
      </div>
    );
  }

  return (
    <Container
      $userBridgePermissions={userBridgePermissions}
      $modulePermissions={modulePermissions}
      $stateColor={stateColor()}
    >
      <AppCard bodyClassName="task-default">
        <div
          className="deadline-block"
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-correctiveActions"
              name={
                !level1?.[taskID]?.closedStamp
                  ? 'module.ca.limitStamp'
                  : 'closed.task'
              }
            />
          )}
        >
          {level1?.[taskID]?.closedStamp ? (
            <AppRemixIcon icon="check" size={20} />
          ) : (
            <>
              <div>{level1?.[taskID]?.limitStamp?.split('/')?.[0]}</div>
              <div>{level1?.[taskID]?.limitStamp?.split('/')?.[1]}</div>
              <div>{level1?.[taskID]?.limitStamp?.split('/')?.[2]}</div>
            </>
          )}
        </div>
        <div className="task-block">
          <div className="title-block">
            <div>
              <span className="title-group">
                <small className="mr-1">
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.code"
                  />
                  :
                </small>
                <span>{level1?.[taskID]?.code}</span>
              </span>
            </div>
            <div className="dates-rating-block">
              <span className="title-group">
                <small className="mr-1">
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.maker"
                  />
                  :
                </small>
                <span>{level1?.[taskID]?.userName}</span>
              </span>
              <span className="title-group">
                <small className="mr-1">
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.dateStamp"
                  />
                  :
                </small>
                <span>{level1?.[taskID]?.dateStamp}</span>
              </span>
              <span className="title-group">
                <small className="mr-1">
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.edit-closedStamp"
                  />
                  :
                </small>
                <span>{level1?.[taskID]?.closedStamp ?? '-'}</span>
              </span>
              {modulePermissions?.reviews && (
                <div className="star-group">{getReview()}</div>
              )}
            </div>
          </div>
          <Scrollbars style={{ height: '100px', flexGrow: 1 }}>
            <div
              className="pr-2"
              style={{ minHeight: '100px' }}
              dangerouslySetInnerHTML={{
                __html: level1?.[taskID]?.description,
              }}
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.description"
                />
              )}
            />
          </Scrollbars>
        </div>
        {modulePermissions?.systemCenters && (
          <div className="system-centers-block">
            <div>
              <h3>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.systems"
                />
              </h3>
              <ul>
                {level2?.[taskID]?.systems.map((s) => (
                  <li key={s?.id}>
                    {s?.shortName} {s?.name && `(${s?.name})`}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.centers"
                />
              </h3>
              <ul>
                {level2?.[taskID]?.correctiveActionPlants.map((cap) => (
                  <li key={cap?.id}>{cap?.name}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {(modulePermissions?.source || modulePermissions?.taskType) && (
          <div className="source-task-type-block">
            {modulePermissions?.source && (
              <div>
                <h3>
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.source"
                  />
                </h3>
                <div>{level2?.[taskID]?.source}</div>
              </div>
            )}
            {modulePermissions?.taskType && (
              <div>
                <h3>
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.task-type"
                  />
                </h3>
                <div title={level2?.[taskID]?.type?.shortName}>
                  {level2?.[taskID]?.type?.name}
                </div>
              </div>
            )}
          </div>
        )}
        {modulePermissions?.responsibles && (
          <div className="responsibles-block">
            <h3>
              <Translator
                file="module-correctiveActions"
                name="module.ca.employee-s"
              />
            </h3>
            <ul>
              {level2?.[taskID]?.responsibles?.map((r) => (
                <li key={r.id}>
                  {r.name}
                  {r?.responsibles && (
                    <ul>
                      {r?.responsibles.map((sb) => (
                        <li key={sb.id}>{sb.name}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="poBlock-icons">
          <div className="icon-grid">
            <IconListBlock taskID={taskID} />
          </div>
        </div>
        <div
          className="poBlock-init-end bg-primary pointer"
          onClick={() => setIsOpened(!isOpened)}
        >
          <AppRemixIcon icon={isOpened ? 'subtract' : 'add'} color="white" />
        </div>
        <div />
      </AppCard>
      <UnmountClosed isOpened={isOpened}>
        <ExpandedContainer taskID={taskID} />
      </UnmountClosed>
    </Container>
  );
}

const Container = styled.div`
  border-radius: 3px;
  .task-default{
    padding: 2px;
    height: 160px;
    display: grid;
    grid-template-columns: 
    40px // State
    minmax(310px, 1fr) // Task
    ${(props) =>
      props.$modulePermissions?.systemCenters && '170px'} // Systems / Centers
    ${(props) =>
      (props.$modulePermissions?.source ||
        props.$modulePermissions?.taskType) &&
      '150px'} // Source / Task type
    ${(props) =>
      props.$modulePermissions?.responsibles && '150px'} // Responsibles
    46px //IconList
    40px; //Expand
    overflow: hidden;
    border: 1px solid ${(props) => props.$stateColor};
    h3{
      font-weight: normal;
      color: ${colors.primaryLight}
    }
    &>div{
     border-right: 1px solid ${colors.grey300};
    }
    &>.deadline-block{
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #fff;
      background-color: ${(props) => props.$stateColor};
      font-weight: bold;
      height: 154px;
      overflow: hidden;
    }
    &>.task-block{
      display: flex;
      flex-direction: column;
      padding: 0 10px 10px 10px;
      overflow: hidden;
      border-radius: 2px;
      font-size: 13px;
      border-right: 1px solid $gray-300;
      padding-top: 0;
      .title-block{
        display: flex;
        gap: 15px;
        justify-content: space-between;
        .title-group{
            white-space: nowrap;
            text-align: center;
            font-weight: bold;
            color: ${colors.primaryLight};
            margin-right: 10px;
        }
        .dates-rating-block {
            display: flex;
            flex-wrap: wrap;
            flex-grow: 1;
            justify-content: end;
            .star-group {
                display: flex;
                justify-content: end;
                margin-right: 10px;
            }
        }
      }
    }
    &>.system-centers-block,
    &>.source-task-type-block {
      padding: 0 10px 10px 10px;
      border-right: 1px solid $gray-300;
      font-size: 11px;
      overflow: auto;
      ul {
        padding-left: 15px;
      }
    }
    &>.source-task-type-block {
        &>div{height: 70px;}
    }
    &>.responsibles-block {
        padding: 0 10px 10px 10px;
        border-right: 1px solid $gray-300;
        font-size: 11px;
        overflow: auto;
        ul {
            padding-left: 15px;
        }
        &>ul>li>ul {
            margin-bottom: 10px;
        }
    }
    &>.poBlock-init-end{
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 2px;
      font-size: 30px;
      padding: 0;
      height: 154px;
    }
    
    &>.block-{
      padding: 0 10px 10px 10px;
      border-right: 1px solid $gray-300;
      font-size: 11px;
      &>div{height: 70px;}
    }
    &>.poBlock-calendar{
      padding: 10px;
    }
    &>.poBlock-icons{
      height: 90%;
      display: grid;
      grid-template-rows: 1fr 30px;
      .icon-grid{
        padding: 0 3px 3px 3px;
        display: grid;
        grid-template-columns: 20px 20px;
        grid-template-rows: 20px 20px 20px 20px 20px 20px;
        height: 70%;
        &>i{
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }
    &>.poBlock-probSevRisk{
      max-height: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      font-size: 12px;
      font-weight: bold;
      color: ${colors.primary};
      span{
        padding: 10px 0;
      }
    }
    &>.poBlock-impactProb{
      max-height: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      font-size: 12px;
      font-weight: bold;
      color: ${colors.primary};
      justify-content: center;
      span{
        padding: 10px 0;
      }
    }
  }
  h3{
    font-size: 13px;
    color: $primary;
    margin: 0 0 6px 0;
    font-weight: bold;
  }
  h4{
    font-size: 10px;
    color: $primary;
    text-transform: uppercase;
  }
}
`;
